import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  SbCard,
  SbSpin,
  SbSelect,
  SbSwitch,
  SbTabs,
  SbNotification,
  SbTag,
  SbDropdown,
  SbTooltip,
  SbInfoTooltip,
  TooltipTriggerStyled,
} from "uiCore";
import { ArrowLeftOutlined, CaretDownOutlined } from "uiCore";

import useAuthStore from "Store/useAuthStore";
import useEntitlementsStore from "Store/useEntitlementsStore";
import { useHasAccess } from "Routes/hooks/useHasAccess";
import usePAICStore from "Store/usePAICStore";
import useTourStore from "Store/useTourStore";
import SbTour from "uiCore/SbTour/SbTour";
import buildTourSteps from "uiCore/SbTour/buildTourSteps";
import useReadyTour from "uiCore/SbTour/useReadyTour";
import { GROUP_AGENTS_TOUR_STEPS, CROSS_PAGE_TOUR_PHASE } from "../dashboard/PeoplePlusAIV2.constants";

import {
  getPAICAgents,
  getPAICAgentsByRoles,
  getPAICSelectedAgentsStats,
  getPAICAgentSelections,
  insertPAICSelectedAgents,
  resubmitPAICSelection,
} from "Api/apiService";

import Permission from "Components/Permission";
import analytics from "Utils/analytics";

import PACGroupAgentsTableV2 from "./PACGroupAgentsTableV2";
import {
  GROUP_AGENT_TABS,
  PEOPLE_PLUS_AI_DROPDOWN_OPTIONS,
  DOCUMENT_TYPE_OPTIONS,
} from "constants/groupAgents.constants";
import {
  PAIC_SELECTION_STATUS,
  PAIC_SELECTION_STATUS_COLOR,
  PAIC_STATS_PRIMARY_ITEMS,
  PAIC_STATS_SECONDARY_ITEMS,
  PAIC_DOCUMENT_TYPES,
  PAIC_PROJECT_SOURCE,
  PAIC_VIEW_ONLY_MESSAGE,
  PAIC_ERROR_MESSAGES,
  handlePAICErrorCode,
} from "constants/peoplePlusAI.constants";
import { PAIC_ROUTES, buildJdPrdRoute } from "constants/paicRoutes.constants";

import {
  PageContainer,
  HeaderSection,
  HeaderTopRow,
  TitleRow,
  BackButton,
  PageTitle,
  GroupNameBadge,
  TabsAndControlsBlock,
  TabsRow,
  ControlsRow,
  SelectControlWrapper,
  ContentSection,
  MainContent,
  SidebarContent,
  TableSection,
  NoAgentsMsg,
  SelectedAgentsWrap,
  SectionIntro,
  ViewByRow,
  ViewByLeft,
  ViewByLabel,
  ViewByActiveText,
  ViewByInactiveText,
  HelperList,
  HelperListItem,
  SubmitButton,
  OptionRow,
  OptionText,
  ComingSoonText,
  StatsCardContainer,
  StatsCardHeader,
  StatsCardTitle,
  StatsCardSubtitle,
  StatsCardBody,
  StatsPrimaryRow,
  StatsBigValue,
  StatsLabel,
  StatsRoiBadge,
  StatsTileGrid3,
  StatsTileGrid2,
  StatsTile,
  StatsTileLabel,
  StatsTileValue,
  TabLabelWithCaret,
} from "./SbPACGroupAgentsV2.styles";

// Tooltip content strings (PRD §5.11)
const TOOLTIP_BACK_BUTTON = "Return to the PAIC Dashboard.";
const TOOLTIP_VIEW_BY_TOGGLE =
  "Switch between viewing AI agents (grouped by capability) and viewing affected roles (grouped by role with their impacted agents).";
const TOOLTIP_SELECT_VERSION =
  "Switch between saved versions (combinations of selected agents). Each version models a different implementation scenario.";
const TOOLTIP_DOCUMENT_TYPE =
  "Choose what type of document to view or download: Agent Job Descriptions, Agent PRDs, People (+AI) JDs, New Org Design, or Agent Architecture.";
const TOOLTIP_SUBMIT_BUTTON =
  "Save your current agent selection as a new version and generate role impact analysis and agent documents. Takes 10–30 minutes.";
const TOOLTIP_RE_SUBMIT_BUTTON =
  "The previous version submission failed. Retry with the same agent selection.";
const TOOLTIP_POTENTIAL_VALUE_CARD =
  "The estimated annual monetary value that could be realised by deploying the selected AI agents across impacted roles.";
const TOOLTIP_ROI_CARD =
  "Estimated Return on Investment: potential value divided by estimated development cost of the selected agents.";
const TOOLTIP_AI_POTENTIAL_CARD =
  "The weighted average percentage of work across all affected roles that could be augmented or automated by the selected AI agents.";
const TOOLTIP_EST_DEV_COST_CARD =
  "Estimated cost to build or procure the selected AI agents, based on agent complexity and industry benchmarks.";

const PAIC_STATS_SECONDARY_TOOLTIPS = {
  aiPotential: TOOLTIP_AI_POTENTIAL_CARD,
  estimatedCost: TOOLTIP_EST_DEV_COST_CARD,
};

const SelectionStatusTag = ({ status }) => (
  <SbTag color={PAIC_SELECTION_STATUS_COLOR[status] ?? "default"}>
    {status}
  </SbTag>
);

const StatsSummaryCard = ({
  title,
  subtitle,
  selectedAgentsContent = null,
  potentialValue,
  roiValue,
  rolesCount,
  positionsCount,
  agentsCount,
  aiPotential,
  estimatedCost,
  hasCompEntitlement = true,
  scrollable = false,
}) => {
  const primaryStatsMap = {
    roles: rolesCount ?? 0,
    positions: positionsCount ?? 0,
    agents: agentsCount ?? 0,
  };

  const secondaryStatsMap = {
    aiPotential,
    estimatedCost,
  };

  return (
    <StatsCardContainer $scrollable={scrollable}>
      <SbCard
        className="paic-stats-card"
        title={
          <StatsCardHeader>
            <StatsCardTitle>{title}</StatsCardTitle>
            {subtitle && <StatsCardSubtitle>{subtitle}</StatsCardSubtitle>}
          </StatsCardHeader>
        }
      >
        <StatsCardBody>
          {selectedAgentsContent && <div>{selectedAgentsContent}</div>}

          <StatsPrimaryRow>
            <div>
              <StatsBigValue>
                {hasCompEntitlement ? potentialValue : "-"}
              </StatsBigValue>
              <StatsLabel>
                <SbInfoTooltip content={TOOLTIP_POTENTIAL_VALUE_CARD}>
                  Potential Value
                </SbInfoTooltip>
              </StatsLabel>
            </div>
            <SbTooltip content={TOOLTIP_ROI_CARD}>
              <StatsRoiBadge>
                {hasCompEntitlement ? roiValue : "ROI -"}
              </StatsRoiBadge>
            </SbTooltip>
          </StatsPrimaryRow>

          <StatsTileGrid3>
            {PAIC_STATS_PRIMARY_ITEMS.map(({ key, label }) => (
              <StatsTile key={key}>
                <StatsTileLabel>{label}</StatsTileLabel>
                <StatsTileValue>{primaryStatsMap[key]}</StatsTileValue>
              </StatsTile>
            ))}
          </StatsTileGrid3>

          <StatsTileGrid2>
            {PAIC_STATS_SECONDARY_ITEMS.map(({ key, label }) => (
              <StatsTile key={key}>
                <StatsTileLabel>
                  <SbInfoTooltip
                    content={PAIC_STATS_SECONDARY_TOOLTIPS[key]}
                  >
                    {label}
                  </SbInfoTooltip>
                </StatsTileLabel>
                <StatsTileValue>
                  {key === "estimatedCost" && !hasCompEntitlement
                    ? "-"
                    : secondaryStatsMap[key]}
                </StatsTileValue>
              </StatsTile>
            ))}
          </StatsTileGrid2>
        </StatsCardBody>
      </SbCard>
    </StatsCardContainer>
  );
};

const SbPACGroupAgentsV2 = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const email = useAuthStore((s) => s.email);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const entitlements = useEntitlementsStore((s) => s.entitlements);
  const { hasAccess } = useHasAccess();

  const canViewSkillsBridger = hasAccess("skillsbridger", ["view"]);

  const hasCompEntitlement = entitlements
    ?.find((e) => e.name === "people_plus_ai")
    ?.entitlements?.includes("view_comp");

  const projectId = usePAICStore((s) => s.projectId);
  const projectSource = usePAICStore((s) => s.projectSource);
  const loading = usePAICStore((s) => s.loading);
  const setLoading = usePAICStore((s) => s.setLoading);
  const groupName = usePAICStore((s) => s.groupName);
  const setAgentId = usePAICStore((s) => s.setAgentId);
  const setSelectionId = usePAICStore((s) => s.setSelectionId);
  const setRoleSBUniqueId = usePAICStore((s) => s.setRoleSBUniqueId);
  const setRolePositionCount = usePAICStore((s) => s.setRolePositionCount);
  const setRoleTotalComp = usePAICStore((s) => s.setRoleTotalComp);
  const viewByToggle = usePAICStore((s) => s.viewByToggle);
  const setViewByToggle = usePAICStore((s) => s.setViewByToggle);
  const selectedVersionNumber = usePAICStore((s) => s.selectedVersionNumber);
  const setSelectedVersionNumber = usePAICStore(
    (s) => s.setSelectedVersionNumber,
  );
  const setSelectedVersionWithAgents = usePAICStore(
    (s) => s.setSelectedVersionWithAgents,
  );

  const [activeTab, setActiveTab] = useState("people-plus-ai");
  const [PAICAgents, setPAICAgents] = useState([]);
  const [PAICRoles, setPAICRoles] = useState([]);
  const [groupStats, setGroupStats] = useState({});
  const [selectedStats, setSelectedStats] = useState({});
  const [selectedAgentIds, setSelectedAgentIds] = useState(new Set());
  const [allAgentSelections, setAllAgentSelections] = useState([]);
  const [selectedDocumentType, setSelectedDocumentType] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // ── Guided tour: Group Agents leg (handed off from the PAIC dashboard) ──────
  const crossPageTourPhase = useTourStore((s) => s.crossPageTourPhase);
  const setCrossPageTourPhase = useTourStore((s) => s.setCrossPageTourPhase);
  const statsRef = useRef(null);
  const versionRef = useRef(null);
  const docTypeRef = useRef(null);
  const gaTourTargetRefs = {
    statsPanel: statsRef,
    versionControls: versionRef,
    docType: docTypeRef,
  };
  const [gaTourStep, setGaTourStep] = useState(0);
  const gaTourActive =
    crossPageTourPhase === CROSS_PAGE_TOUR_PHASE.GROUP_AGENTS &&
    !loading &&
    activeTab === "people-plus-ai";
  const { open: gaTourOpen, remountKey: gaTourKey } = useReadyTour({
    active: gaTourActive,
    ready: !loading,
  });

  // Ending this leg (finish or dismiss) returns to the dashboard, which resumes
  // the main tour at its Finish step. We deliberately DON'T preserve the group
  // here (unlike the Back button) so the dashboard's mount effect clears the
  // group we auto-selected just to drive the tour — leaving a clean state.
  const endGroupAgentsTour = () => {
    setCrossPageTourPhase(CROSS_PAGE_TOUR_PHASE.FINISH);
    navigate(PAIC_ROUTES.MAIN);
  };

  const {
    steps: gaBuiltSteps,
    actionsRender: renderGaActions,
    indicatorsRender: renderGaIndicators,
  } = buildTourSteps(GROUP_AGENTS_TOUR_STEPS, {
    resolveTarget: (step) => gaTourTargetRefs[step.key]?.current,
    greeting: { icon: () => null, onPrimary: () => {}, onSecondary: () => {} },
  });

  const isReadOnly = projectSource === PAIC_PROJECT_SOURCE.SHARED;

  const ShowOnlyForHoursPermission = ({ children }) => {
    const hasComp = entitlements
      ?.find((e) => e.name === "people_plus_ai")
      ?.entitlements?.includes("view_comp");
    const hasHours = entitlements
      ?.find((e) => e.name === "people_plus_ai")
      ?.entitlements?.includes("view_hours");

    if (hasHours && !hasComp) return <>{children}</>;
    return null;
  };

  const formatCurrencyShort = (value) => {
    const num = Number(value);
    if (isNaN(num)) return "$0";
    return `$ ${Math.round(num).toLocaleString()}`;
  };

  const formatPercentage = (value) => {
    const num = Number(value);
    if (isNaN(num)) return "0%";
    return `${(num * 100).toFixed(1)}%`;
  };

  const handleOnBackClick = () => {
    analytics.trackPAICBackToMain({
      from: "pac_group_agents",
      email,
      description: "Navigated back to main PAIC page",
    });
    if (location.state?.fromAgentLibrary) {
      navigate(PAIC_ROUTES.AGENT_LIBRARY, {
        state: { page: location.state.agentLibraryPage },
      });
      return;
    }
    navigate(PAIC_ROUTES.MAIN, {
      state: { preserveSelectedGroup: true },
    });
  };

  const handleTabClick = (tabKey) => {
    if (tabKey === "skills-bridger" && canViewSkillsBridger) {
      navigate("/skills-bridger");
      return;
    }

    const previousTab = activeTab;
    setActiveTab(tabKey);
    analytics.trackPAICTabChanged({
      from: "pac_group_agents",
      email,
      previousTab,
      newTab: tabKey,
      description: `Changed tab from ${previousTab} to ${tabKey}`,
    });
  };

  const handleRowClick = (agentId) => {
    if (loading) return;

    const agent = PAICAgents.find(
      (item) => item.sb_agent_unique_id === agentId,
    );

    analytics.trackPAICAgentRowClicked({
      from: "pac_group_agents",
      email,
      agentId,
      agentName: agent?.ai_agent_name,
      description: `Clicked agent row: ${agent?.ai_agent_name}`,
    });

    setAgentId(agentId);
    navigate(PAIC_ROUTES.AGENT_DETAILS, {
      state: { fromParent: true },
    });
  };

  const handleRoleRowClick = (role) => {
    if (loading) return;

    analytics.trackPAICRoleRowClicked({
      from: "pac_group_agents",
      email,
      roleId: role.sb_unique_id,
      roleName: role.job_title,
      description: `Clicked role row: ${role.job_title}`,
    });

    const matchedVersion = getMatchedVersion();

    if (matchedVersion?.sb_selection_unique_id) {
      setSelectionId(matchedVersion.sb_selection_unique_id);
    }

    setRoleSBUniqueId(role.sb_unique_id);
    setRolePositionCount(role.role_position_count ?? role.total_positions ?? 0);
    setRoleTotalComp(role.total_comp ?? 0);

    navigate(PAIC_ROUTES.ROLE_IMPACT, {
      state: { fromParent: true },
    });
  };

  const showViewOnlyNotification = () => {
    SbNotification.info(PAIC_VIEW_ONLY_MESSAGE.title, PAIC_VIEW_ONLY_MESSAGE.description);
  };

  const toggleAgentSelection = async (agentId) => {
    if (loading) return;
    if (isReadOnly) { showViewOnlyNotification(); return; }

    const nextSelection = new Set(selectedAgentIds);
    const agent = PAICAgents.find(
      (item) => item.sb_agent_unique_id === agentId,
    );

    if (nextSelection.has(agentId)) {
      nextSelection.delete(agentId);
      analytics.trackPAICDeselectAgent({
        from: "pac_group_agents",
        email,
        agentId,
        agentName: agent?.ai_agent_name,
        description: `Deselected agent: ${agent?.ai_agent_name}`,
      });
    } else {
      nextSelection.add(agentId);
      analytics.trackPAICSelectAgent({
        from: "pac_group_agents",
        email,
        agentId,
        agentName: agent?.ai_agent_name,
        description: `Selected agent: ${agent?.ai_agent_name}`,
      });
    }

    setSelectedAgentIds(nextSelection);

    if (nextSelection.size > 0) {
      await fetchSelectedAgentsAnalysis(Array.from(nextSelection));
    } else {
      setSelectedStats({});
    }
  };

  const handleSelectAll = async (isChecked) => {
    if (loading) return;
    if (isReadOnly) { showViewOnlyNotification(); return; }

    const nextSelection = isChecked
      ? new Set(PAICAgents.map((a) => a.sb_agent_unique_id))
      : new Set();

    analytics.trackPAICSelectAllAgents({
      from: "pac_group_agents",
      email,
      selected: isChecked,
      agentCount: PAICAgents.length,
      description: `${isChecked ? "Selected" : "Deselected"} all ${
        PAICAgents.length
      } agents`,
    });

    setSelectedAgentIds(nextSelection);

    if (nextSelection.size > 0) {
      await fetchSelectedAgentsAnalysis(Array.from(nextSelection));
    } else {
      setSelectedStats({});
    }
  };

  const selectedAgentsList = useMemo(
    () =>
      PAICAgents.filter((agent) =>
        selectedAgentIds.has(agent.sb_agent_unique_id),
      ),
    [PAICAgents, selectedAgentIds],
  );

  const fetchPAICAgents = async () => {
    if (!projectId) {
      setPAICAgents([]);
      setGroupStats({});
      return;
    }

    setLoading(true);
    try {
      const response = await getPAICAgents(projectId);
      setPAICAgents(response?.data || []);
      setGroupStats(response?.group_totals || {});
    } catch (error) {
      setPAICAgents([]);
      setGroupStats({});
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error(PAIC_ERROR_MESSAGES.LOAD_AGENTS_FAILED);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchPAICRoles = async (selectionId) => {
    if (!selectionId) return;

    setLoading(true);
    try {
      const response = await getPAICAgentsByRoles(selectionId);
      setPAICRoles(response || []);
    } catch (error) {
      setPAICRoles([]);
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error(PAIC_ERROR_MESSAGES.LOAD_ROLES_FAILED);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSelectedAgentsAnalysis = async (agentIds) => {
    setLoading(true);
    try {
      const response = await getPAICSelectedAgentsStats({
        agent_ids: agentIds,
      });
      setSelectedStats(response || {});
    } catch (error) {
      handlePAICErrorCode(error, SbNotification.error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAgentSelections = async (restoreVersion = false) => {
    if (!projectId) {
      setAllAgentSelections([]);
      return;
    }

    setLoading(true);
    try {
      const response = await getPAICAgentSelections(projectId);

      if (response?.length > 0) {
        setAllAgentSelections(response);

        if (restoreVersion && selectedVersionNumber !== null) {
          const toRestore = response.find(
            (v) => v.version === selectedVersionNumber,
          );

          if (toRestore?.agent_unique_ids) {
            const ids = new Set(toRestore.agent_unique_ids);
            setSelectedAgentIds(ids);

            if (ids.size > 0) {
              await fetchSelectedAgentsAnalysis(Array.from(ids));
            }

            if (
              toRestore.selection_status === PAIC_SELECTION_STATUS.COMPLETED &&
              toRestore.sb_selection_unique_id
            ) {
              await fetchPAICRoles(toRestore.sb_selection_unique_id);
            }

            if (
              toRestore.selection_status === PAIC_SELECTION_STATUS.PROCESSING
            ) {
              setViewByToggle(false);
            }
          }
        }
      } else {
        setAllAgentSelections([]);
      }
    } catch (error) {
      setAllAgentSelections([]);
      handlePAICErrorCode(error, SbNotification.error);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionsDropdownOpen = async (open) => {
  if (open && projectId) {
    await fetchAgentSelections(false);
  }
};

  const handleSubmitSelectedAgents = async () => {
    if (isSubmitting || selectedAgentIds.size === 0 || !projectId) return;

    setIsSubmitting(true);
    setLoading(true);
    try {
      const payload = {
        agent_ids: Array.from(selectedAgentIds),
        project_id: projectId,
      };

      const response = await insertPAICSelectedAgents(payload, refreshToken);

      if (response) {
        analytics.trackPAICSubmitAgents({
          from: "pac_group_agents",
          email,
          agentCount: selectedAgentIds.size,
          description: `Submitted ${selectedAgentIds.size} selected agents`,
        });

        SbNotification.success("Selected agents submitted successfully");
        await fetchAgentSelections();
        await fetchPAICAgents();
      }
    } catch (error) {
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error(PAIC_ERROR_MESSAGES.SUBMIT_AGENTS_FAILED);
      }
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const handleResubmitSelection = async () => {
    if (isSubmitting || !matchedVersionObject?.sb_selection_unique_id || !refreshToken) return;

    setIsSubmitting(true);
    setLoading(true);
    try {
      const payload = {
        selection_id: matchedVersionObject.sb_selection_unique_id,
        refresh_token: refreshToken,
      };

      const response = await resubmitPAICSelection(payload);

      if (response) {
        analytics.trackPAICSubmitAgents({
          from: "pac_group_agents",
          email,
          agentCount: selectedAgentIds.size,
          description: `Re-submitted failed selection`,
        });

        SbNotification.success("Selection re-submitted successfully");
        await fetchAgentSelections();
        await fetchPAICAgents();
      }
    } catch (error) {
      if (!handlePAICErrorCode(error, SbNotification.error)) {
        SbNotification.error("Unable to re-submit selection");
      }
    } finally {
      setIsSubmitting(false);
      setLoading(false);
    }
  };

  const getMatchedVersion = () => {
    if (!allAgentSelections.length) return null;

    return allAgentSelections.find((sel) => {
      const stored = sel.agent_unique_ids || [];
      if (stored.length !== selectedAgentIds.size) return false;
      return stored.every((id) => selectedAgentIds.has(id));
    });
  };

  const matchedVersionObject = getMatchedVersion();
  const selectionStatus = matchedVersionObject?.selection_status || null;

  const versionOptions = [...allAgentSelections]
    .sort((a, b) => b.version - a.version)
    .map((v) => {
      const agentCount = v.agent_unique_ids?.length ?? 0;

      return {
        value: v.version,
        key: v.sb_selection_unique_id,
        rawLabel: `Version ${v.version} (${agentCount} Agent/s)`,
        label: (
          <OptionRow key={v.sb_selection_unique_id}>
            <OptionText>{`Version ${v.version} (${agentCount} Agent/s)`}</OptionText>
            <SelectionStatusTag status={v.selection_status} />
          </OptionRow>
        ),
      };
    });

  const isSubmitDisabled =
    selectedAgentIds.size === 0 ||
    isSubmitting ||
    selectionStatus === PAIC_SELECTION_STATUS.PROCESSING ||
    selectionStatus === PAIC_SELECTION_STATUS.COMPLETED ||
    loading;

  const isResubmitMode = selectionStatus === PAIC_SELECTION_STATUS.FAILED;
  const submitButtonText = isResubmitMode ? "Re-Submit" : "Submit";
  const submitButtonHandler = isResubmitMode
    ? handleResubmitSelection
    : handleSubmitSelectedAgents;

  const isDropdownEnabled = selectionStatus === PAIC_SELECTION_STATUS.COMPLETED;

  const handleVersionChange = (versionNum) => {
    if (versionNum === null || versionNum === undefined) {
      setSelectedAgentIds(new Set());
      setSelectedStats({});
      setPAICRoles([]);
      setSelectedVersionNumber(null);
      return;
    }

    const sel = allAgentSelections.find((v) => v.version === versionNum);

    if (sel?.agent_unique_ids) {
      analytics.trackPAICVersionSelected({
        from: "pac_group_agents",
        email,
        version: versionNum,
        description: `Selected version ${versionNum}`,
      });

      const ids = new Set(sel.agent_unique_ids);
      setSelectedAgentIds(ids);
      setSelectedVersionNumber(versionNum);

      if (ids.size > 0) {
        fetchSelectedAgentsAnalysis(Array.from(ids));
      }

      if (
        sel.selection_status === PAIC_SELECTION_STATUS.COMPLETED &&
        sel.sb_selection_unique_id
      ) {
        fetchPAICRoles(sel.sb_selection_unique_id);
      } else {
        setPAICRoles([]);
      }

      if (sel.selection_status === PAIC_SELECTION_STATUS.PROCESSING) {
        setViewByToggle(false);
      }
    }
  };

  const handleDocumentTypeChange = (selected) => {
    setSelectedDocumentType(selected);

    analytics.trackPAICDocumentTypeSelected({
      from: "pac_group_agents",
      email,
      documentType: selected,
      description: `Selected document type: ${selected}`,
    });

    if (
      selected === PAIC_DOCUMENT_TYPES.AGENT_PRD ||
      selected === PAIC_DOCUMENT_TYPES.AGENT_JD
    ) {
      const agentBuilds = PAICAgents.reduce((map, agent) => {
        if (agent.sb_agent_unique_id && agent.build_id && agent.build_status) {
          map[agent.sb_agent_unique_id] = { build_id: agent.build_id, build_status: agent.build_status };
        }
        return map;
      }, {});
      navigate(buildJdPrdRoute(selected), { state: { agentBuilds } });
    }

    if (selected === PAIC_DOCUMENT_TYPES.PEOPLE_JD) {
      if (!matchedVersionObject?.sb_selection_unique_id) {
        console.error("No matched version found");
        return;
      }

      navigate(PAIC_ROUTES.PEOPLE_JD_VIEW, {
        state: { selectionId: matchedVersionObject.sb_selection_unique_id },
      });
    }
  };

  const peopleAIMenuItems = PEOPLE_PLUS_AI_DROPDOWN_OPTIONS.map((item) => ({
    key: item.key,
    label: item.label,
  }));

  const tabItems = GROUP_AGENT_TABS.map((tab) => {
    // if (tab.key === "people-plus-ai") {
    //   return {
    //     key: tab.key,
    //     label: (
    //       <SbDropdown menu={{ items: peopleAIMenuItems }} trigger={["click"]}>
    //         <TabLabelWithCaret onClick={(e) => e.stopPropagation()}>
    //           {tab.label}
    //           <CaretDownOutlined />
    //         </TabLabelWithCaret>
    //       </SbDropdown>
    //     ),
    //   };
    // }

    return { key: tab.key, label: tab.label };
  });

  const handleSbTabsChange = (tabKey) => {
    if (tabKey === "people-plus-ai") {
      setActiveTab("people-plus-ai");
      return;
    }

    handleTabClick(tabKey);
  };

  useEffect(() => {
    if (!projectId) return;

    fetchPAICAgents();
    fetchAgentSelections(location.state?.fromParent || false);
  }, [projectId]);

  useEffect(() => {
    if (matchedVersionObject?.version) {
      setSelectedVersionNumber(matchedVersionObject.version);
    }
  }, [matchedVersionObject, setSelectedVersionNumber]);

  // Note: fetchPAICRoles is called explicitly in handleVersionChange and
  // fetchAgentSelections(restoreVersion). Removed the useEffect here to
  // prevent duplicate API calls (and duplicate error notifications).

  useEffect(() => {
    if (!location.state?.fromParent) {
      setViewByToggle(false);
    }
  }, [location.pathname, location.state, setViewByToggle]);

  useEffect(() => {
    if (matchedVersionObject) {
      setSelectedVersionWithAgents(matchedVersionObject);
    }
  }, [matchedVersionObject, setSelectedVersionWithAgents]);

  const renderComingSoon = (label) => (
    <SectionIntro>
      <ComingSoonText>{label} is coming soon.</ComingSoonText>
    </SectionIntro>
  );

  return (
    <SbSpin spinning={loading}>
    <PageContainer>
      <HeaderSection>
        <HeaderTopRow>
          <TitleRow>
            <SbTooltip content={TOOLTIP_BACK_BUTTON}>
              <BackButton onClick={handleOnBackClick} type="button">
                <ArrowLeftOutlined />
              </BackButton>
            </SbTooltip>
            <PageTitle>
              Group : <GroupNameBadge>{groupName}</GroupNameBadge>
            </PageTitle>
          </TitleRow>
        </HeaderTopRow>

        <TabsAndControlsBlock>
          <TabsRow>
            <SbTabs
              activeKey={activeTab}
              onChange={handleSbTabsChange}
              items={tabItems}
            />
            <ControlsRow>
              <SbTooltip content={TOOLTIP_SELECT_VERSION}>
                <SelectControlWrapper $width="280px" ref={versionRef}>
                  <SbSelect
                    placeholder="Select Version"
                    options={versionOptions}
                    value={
                      matchedVersionObject ? matchedVersionObject.version : null
                    }
                    allowClear
                    optionLabelProp="rawLabel"
                    onChange={handleVersionChange}
                    onDropdownVisibleChange={handleVersionsDropdownOpen}
                  />
                </SelectControlWrapper>
              </SbTooltip>

              <SbTooltip content={TOOLTIP_DOCUMENT_TYPE}>
                <SelectControlWrapper $width="220px" ref={docTypeRef}>
                  <SbSelect
                    placeholder="New Org Design"
                    disabled={!isDropdownEnabled}
                    options={DOCUMENT_TYPE_OPTIONS}
                    value={selectedDocumentType}
                    onChange={handleDocumentTypeChange}
                  />
                </SelectControlWrapper>
              </SbTooltip>
            </ControlsRow>
          </TabsRow>
        </TabsAndControlsBlock>
      </HeaderSection>

      <ContentSection>
        <MainContent>
          {activeTab === "people-plus-ai" && (
            <>
              <SectionIntro>
                <ViewByRow>
                  <ViewByLeft>
                    <ViewByLabel>View By:</ViewByLabel>
                    <ViewByActiveText $active={!viewByToggle}>
                      Agents
                    </ViewByActiveText>

                    <SbTooltip content={TOOLTIP_VIEW_BY_TOGGLE}>
                      <TooltipTriggerStyled>
                        <SbSwitch
                          checked={viewByToggle}
                          disabled={selectionStatus !== "Completed"}
                          onChange={(checked) => {
                            setViewByToggle(checked);
                            analytics.trackPAICViewByToggle({
                              from: "pac_group_agents",
                              email,
                              viewMode: checked ? "roles" : "agents",
                              description: `Switched view to ${
                                checked ? "roles" : "agents"
                              }`,
                            });
                          }}
                        />
                      </TooltipTriggerStyled>
                    </SbTooltip>

                    <ViewByInactiveText $active={viewByToggle}>
                      Roles
                    </ViewByInactiveText>
                  </ViewByLeft>

                  {!viewByToggle && (
                    <SbTooltip
                      content={
                        isReadOnly
                          ? PAIC_VIEW_ONLY_MESSAGE.description
                          : isResubmitMode
                            ? TOOLTIP_RE_SUBMIT_BUTTON
                            : TOOLTIP_SUBMIT_BUTTON
                      }
                    >
                      <SubmitButton
                        $disabled={isReadOnly || (isSubmitDisabled && !isResubmitMode)}
                        onClick={isReadOnly ? showViewOnlyNotification : submitButtonHandler}
                        type="button"
                      >
                        {submitButtonText}
                      </SubmitButton>
                    </SbTooltip>
                  )}
                </ViewByRow>

                <HelperList>
                  <HelperListItem>
                    Use this screen to prioritize and select AI agents for the
                    chosen group of roles. For job role specific details, use
                    the toggle above.
                  </HelperListItem>
                  <HelperListItem>
                    Sort agents using the up and down arrows. Click a row to
                    view or rename an agent.
                  </HelperListItem>
                  <HelperListItem>
                    Select agents by checking the boxes under Select. Stats will
                    update automatically.
                  </HelperListItem>
                  <HelperListItem>
                    When ready, click Submit to generate documents.
                  </HelperListItem>
                </HelperList>
              </SectionIntro>

              <TableSection>
                <PACGroupAgentsTableV2
                  viewByToggle={viewByToggle}
                  PAICAgents={PAICAgents}
                  PAICRoles={PAICRoles}
                  selectedAgentIds={selectedAgentIds}
                  handleSelectAll={handleSelectAll}
                  toggleAgentSelection={toggleAgentSelection}
                  handleRowClick={handleRowClick}
                  handleRoleRowClick={handleRoleRowClick}
                  formatPercentage={formatPercentage}
                  formatCurrencyShort={formatCurrencyShort}
                />
              </TableSection>
            </>
          )}

          {activeTab === "learning-catalyst" &&
            renderComingSoon("Learning Catalyst")}
          {activeTab === "talent-mobility" &&
            renderComingSoon("Talent Mobility")}
          {activeTab === "jd" && renderComingSoon("JD")}
          {activeTab === "skills-bridger" && renderComingSoon("Skills Bridger")}
        </MainContent>

        {activeTab === "people-plus-ai" && (
          <SidebarContent ref={statsRef}>
            <StatsSummaryCard
              title="GROUP TOTAL"
              subtitle="Group AI Impact"
              hasCompEntitlement={hasCompEntitlement}
              potentialValue={
                <>
                  <Permission resource="people_plus_ai" actions={["view_comp"]}>
                    {formatCurrencyShort(groupStats.total_value_to_business)}
                  </Permission>
                  <ShowOnlyForHoursPermission>
                    {(groupStats.total_positions_impacted || 0) >= 4
                      ? formatCurrencyShort(groupStats.total_value_to_business_c)
                      : "$"}
                  </ShowOnlyForHoursPermission>
                </>
              }
              roiValue={
                <>
                  <Permission resource="people_plus_ai" actions={["view_comp"]}>
                    <>
                      ROI&nbsp;&nbsp;&nbsp;&nbsp;
                      {(Number(groupStats.total_roi) || 0).toFixed(1)}
                    </>
                  </Permission>
                  <ShowOnlyForHoursPermission>
                    {(groupStats.total_positions_impacted || 0) >= 4
                      ? <>
                        ROI&nbsp;&nbsp;&nbsp;&nbsp;
                        {(Number(groupStats.total_roi) || 0).toFixed(1)}
                      </> 
                      : "ROI -"}
                  </ShowOnlyForHoursPermission>
                </>
              }
             
              rolesCount={groupStats.total_roles_impacted}
              positionsCount={groupStats.total_positions_impacted}
              agentsCount={PAICAgents.length}
              aiPotential={formatPercentage(
                groupStats.total_agent_weighted_ai_percent,
              )}
              estimatedCost={
                <>
                  <Permission resource="people_plus_ai" actions={["view_comp"]}>
                    {formatCurrencyShort(groupStats.total_development_cost)}
                  </Permission>
                  <ShowOnlyForHoursPermission>
                    {(groupStats.total_positions_impacted || 0) >= 4
                      ? formatCurrencyShort(groupStats.total_development_cost)
                      : "$"}
                  </ShowOnlyForHoursPermission>
                </>
              }
            />

            <StatsSummaryCard
              title="SELECTED AGENTS"
              scrollable
              hasCompEntitlement={hasCompEntitlement}
              selectedAgentsContent={
                selectedAgentsList.length === 0 ? (
                  <NoAgentsMsg>No agents selected</NoAgentsMsg>
                ) : (
                  <SelectedAgentsWrap>
                    <SbTag>Selected Agents: {selectedAgentsList.length}</SbTag>
                  </SelectedAgentsWrap>
                )
              }
              potentialValue={
                <>
                  <Permission resource="people_plus_ai" actions={["view_comp"]}>
                    {formatCurrencyShort(selectedStats.total_value_to_business)}
                  </Permission>
                  <ShowOnlyForHoursPermission>
                    {(selectedStats.total_positions_impacted || 0) >= 4
                      ? formatCurrencyShort(
                          selectedStats.total_value_to_business_c,
                        )
                      : "$"}
                  </ShowOnlyForHoursPermission>
                </>
              }
              roiValue={
                <>
                  <Permission resource="people_plus_ai" actions={["view_comp"]}>
                    <>
                      ROI&nbsp;&nbsp;&nbsp;&nbsp;
                      {(Number(selectedStats.total_roi) || 0).toFixed(1)}
                    </>
                  </Permission>

                  <ShowOnlyForHoursPermission>
                    {(selectedStats.total_positions_impacted || 0) >= 4 ? (
                      <>
                        ROI&nbsp;&nbsp;&nbsp;&nbsp;
                        {(Number(selectedStats.total_roi) || 0).toFixed(1)}
                      </>
                    ) : (
                      "ROI -"
                    )}
                  </ShowOnlyForHoursPermission>
                </>
              }
              rolesCount={selectedStats.total_roles_impacted}
              positionsCount={selectedStats.total_positions_impacted}
              agentsCount={selectedAgentsList.length}
              aiPotential={formatPercentage(
                selectedStats.total_agent_weighted_ai_percent,
              )}
              estimatedCost={
                <>
                  <Permission resource="people_plus_ai" actions={["view_comp"]}>
                    {formatCurrencyShort(selectedStats.total_development_cost)}
                  </Permission>
                  <ShowOnlyForHoursPermission>
                    {(selectedStats.total_positions_impacted || 0) >= 4
                      ? formatCurrencyShort(
                          selectedStats.total_development_cost,
                        )
                      : "$"}
                  </ShowOnlyForHoursPermission>
                </>
              }
            />
          </SidebarContent>
        )}
      </ContentSection>

      {/* Group Agents leg of the PAIC tour (handed off from the dashboard) */}
      <SbTour
        key={gaTourKey}
        open={gaTourOpen}
        current={gaTourStep}
        onChange={setGaTourStep}
        steps={gaBuiltSteps}
        onClose={endGroupAgentsTour}
        onFinish={endGroupAgentsTour}
        actionsRender={renderGaActions}
        indicatorsRender={renderGaIndicators}
      />
    </PageContainer>
    </SbSpin>
  );
};

export default SbPACGroupAgentsV2;